import { useTranslation } from 'react-i18next';
import { useDebounce, useGetSet, useUnmount, useUpdateEffect } from 'react-use';
import type { SxProps } from '@mui/material';

import { SearchAutocompleteField, useSearchFieldShortcuts } from '@/components/form-controls/SearchField';
import type { SearchHintName } from '@/shared/constants/storage';
import { useSearchHints } from '@/shared/hooks/useSearchHints';

const SearchFieldStyles = {
  width: 480,
};

type SearchInputProps = {
  // eslint-disable-next-line react/boolean-prop-naming
  disabled?: boolean;
  searchQuery?: string;
  searchHintName: SearchHintName;
  setSearchQuery: (searchQuery: string) => void;
  clearSearchQuery: () => void;
  isContextWindowOpen?: boolean;
  placeholder?: string;
  sx?: SxProps;
  instance?: string;
};

export const SearchInput = (props: SearchInputProps) => {
  const { t } = useTranslation('search-input');
  const {
    disabled,
    searchQuery,
    searchHintName,
    setSearchQuery,
    clearSearchQuery,
    isContextWindowOpen = false,
    placeholder = t('placeholder'),
    sx = SearchFieldStyles,
    instance = '',
  } = props;

  const { hintList, addToHintList } = useSearchHints(searchHintName);

  const [getInputValue, setInputValue] = useGetSet('');
  const inputValue = getInputValue();

  const [, cancel] = useDebounce(
    () => {
      setSearchQuery(inputValue);
      addToHintList(inputValue);
    },
    500,
    [inputValue],
  );

  const inputRef = useSearchFieldShortcuts(() => !isContextWindowOpen);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handlePaste: React.ClipboardEventHandler<HTMLInputElement> = event => {
    event.preventDefault();

    const clipboardData = event.clipboardData.getData('text');

    setInputValue(clipboardData.trim());
  };

  const handleClear = () => {
    cancel();
    clearSearchQuery();
    setInputValue('');
  };

  useUnmount(clearSearchQuery);

  useUpdateEffect(() => {
    if (typeof searchQuery !== 'string') {
      return;
    }

    const inputValue = getInputValue();

    if (inputValue !== searchQuery) {
      setInputValue(searchQuery);
    }
  }, [searchQuery]);

  return (
    <SearchAutocompleteField
      disabled={disabled}
      sx={sx}
      inputRef={inputRef}
      value={inputValue}
      placeholder={placeholder}
      onPaste={handlePaste}
      onChange={handleChange}
      onClear={handleClear}
      setInputValue={setInputValue}
      hints={hintList}
      size="small"
      inputProps={{
        'data-testid': `${instance}-search-input`,
      }}
    />
  );
};
